/**
 * Conteúdo educativo dos passos do fluxo guiado.
 * Derivado de /knowledge (curso de finanças pessoais).
 *
 * @typedef {{ lead: string, bullets?: string[], quote?: string, source?: string }} GuidedFlowEnrichment
 */

/** @type {Record<string, GuidedFlowEnrichment>} */
export const GUIDED_FLOW_ENRICHMENT = {
  g1_selfie_day: {
    source: 'knowledge/05-selfie-financeira.md',
    lead:
      'Escolher um dia fixo cria o hábito de fechar o mês com clareza. Sem este ritual, é difícil saber se o teu património está a evoluir.',
    bullets: [
      'A selfie é uma fotografia mensal de todos os saldos e ativos: o ponto de partida para gerir o dinheiro.',
      'Muitas pessoas recebem o salário a meio do mês: o teu “mês financeiro” pode começar nesse dia.',
      'Só conseguimos gerir aquilo que conhecemos.',
    ],
    quote: 'Se te dessem 30 segundos para dizeres quanto dinheiro tens, conseguirias?',
  },
  g10_first_income: {
    source: 'knowledge/07-registo-despesas-receitas.md',
    lead:
      'Sem saber quanto ganhas, não há forma fiável de perceber se estás a poupar ou a gastar demais. O rendimento mensal é o ponto de partida.',
    bullets: [
      'Regista o que entrou este mês (salário, freelance, rendas, etc.).',
      'Podes criar fontes de rendimento e voltar todos os meses com um clique.',
      'A app usa estes valores para calcular a tua taxa de poupança e margem.',
    ],
    quote:
      'Faz uma diferença gigante saber exatamente quanto ganhas e quanto gastas todos os meses.',
  },
  g2_first_selfie: {
    source: 'knowledge/05-selfie-financeira.md',
    lead:
      'A primeira selfie responde à pergunta mais importante: onde estás hoje? Sem este registo, objetivos e orçamentos ficam no escuro.',
    bullets: [
      'Regista contas, investimentos, crypto, dinheiro vivo e dívidas num único momento.',
      'A app calcula o teu património (net worth) e passa a mostrar a evolução mês a mês.',
      'Ver o número motiva, e torna visíveis tendências que passariam despercebidas.',
    ],
    quote: 'Saber que tenho 500€ na conta é tão importante como saber que tenho 100.000€.',
  },
  g11_active_debts: {
    source: 'knowledge (dívidas)',
    lead:
      'Conhecer o que deves (saldo actual, taxa e prazo) é o primeiro passo para um plano de amortização realista.',
    bullets: [
      'Regista créditos habitação, cartões, crédito pessoal e outros passivos.',
      'O património líquido reflecte activos menos dívidas em aberto.',
      'Mais tarde podes simular cenários e priorizar o que pagar primeiro.',
    ],
  },
  g3_first_expenses: {
    source: 'knowledge/07-registo-despesas-receitas.md',
    lead:
      'Registar despesas e receitas é a fundação de tudo: só com números reais consegues perceber para onde vai o dinheiro e onde podes poupar.',
    bullets: [
      'Faz uma diferença gigante saber exatamente quanto ganhas e quanto gastas todos os meses.',
      'Ao registar, apercebes-te de gastos que podes reduzir ou cortar, muitas vezes surpreendentes.',
      'Registar sozinho não chega: precisas de orçamento para saber se foi um bom mês (passo seguinte).',
      'Neste passo, regista pelo menos 3 despesas deste mês para desbloquear o progresso.',
    ],
    quote:
      'Uma das melhores formas de sobrar mais dinheiro é ganhar consciência exata do destino do teu dinheiro.',
  },
  g4_emergency_fund: {
    source: 'knowledge/12-fundo-base.md, knowledge/13-fundo-emergencia.md',
    lead:
      'Depois da primeira selfie, o passo seguinte é o Fundo de Emergência: a tua rede de segurança para imprevistos.',
    bullets: [
      'Escolhes quantos meses de despesas queres cobrir, começa alcançável e sobe depois.',
      'Marcas o dinheiro que já tens guardado nos teus ativos.',
      'Acompanhas quantos meses estás protegido: o número que importa para dormires descansado.',
    ],
    quote:
      'Um pé-de-meia que nos permite viver tranquilos por sabermos que temos dinheiro para qualquer despesa inesperada.',
  },
  g5_category_budgets: {
    source: 'knowledge/09-orcamento-mensal-anual.md',
    lead:
      'Orçamentar é dar um destino a cada euro que ganhas: passas de reagir ao mês para o planear.',
    bullets: [
      'Cada categoria com um teto máximo mostra logo quando te aproximas do limite.',
      'Registar sem orçamento diz o que aconteceu; orçamentar decide o que vai acontecer.',
      'Permite identificar hábitos de consumo e poupanças escondidas.',
    ],
    quote: 'Cada euro que entra na tua casa deve ser um funcionário teu: deve ter uma função e um objetivo.',
  },
  g6_occasional_calendar: {
    source: 'knowledge/08-calendario-anual-despesas.md',
    lead:
      'Despesas como IMI, IUC, seguros ou férias não aparecem todos os meses, mas quando chegam, arruínam o orçamento se não estiverem planeadas.',
    bullets: [
      'O calendário anual lista o que esperas em cada mês e evita “surpresas” no orçamento mensal.',
      'Suavização: somas o total do ano e divides por 12, poupas todos os meses para quando a despesa chegar.',
      'Podes criar um cofre por despesa (ex.: férias em junho, Natal em dezembro) para ver o progresso.',
    ],
    quote:
      'As despesas ocasionais são as que arruínam orçamentos se não forem planeadas.',
  },
  g7_first_smoothing_pot: {
    source: 'knowledge/08-calendario-anual-despesas.md',
    lead:
      'O cofre de suavização é onde vais depositando mês a mês até cobrires a despesa ocasional, sem stress no mês do pagamento.',
    bullets: [
      'Cada cofre está ligado a uma despesa do calendário (ex.: seguro em agosto).',
      'Os depósitos são despesas na categoria sistema «Cofre», vês o acumulado a crescer.',
      'Basta um cofre para começar; podes criar os restantes depois na página Cofres.',
    ],
    quote:
      'Suavizar é poupar um pouco todos os meses para uma despesa grande que já sabes que vem.',
  },
  g8_first_goal: {
    source: 'knowledge/04-objetivos-smart.md',
    lead:
      'Um objectivo SMART transforma uma vontade vaga (“quero poupar mais”) num plano com valor, prazo e passos concretos.',
    bullets: [
      'Específico: o quê? (ex.: mota nova a €6.000, não “ter muito dinheiro”).',
      'Mensurável e temporal: quanto custa e até quando?',
      'Relevante: liga o objectivo aos teus valores, é isso que mantém a motivação.',
    ],
    quote: 'Um objectivo sem prazo é apenas um sonho.',
  },
};

export function getGuidedFlowEnrichment(stepKey) {
  return GUIDED_FLOW_ENRICHMENT[stepKey] ?? null;
}
